import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  ListItemIcon, 
  Typography, 
  Box, 
  InputAdornment, 
  CircularProgress,
  Breadcrumbs,
  Link,
  Checkbox
} from '@mui/material';
import { PiMagnifyingGlass, PiBuildings, PiFolder, PiBooks, PiBriefcase, PiFileText, PiFile } from 'react-icons/pi';
import { apiService } from '../../services/apiService';

interface Library {
  id: string;
  name: string;
  description: string;
  type: 'library';
}

interface Workspace {
  id: string;
  name: string;
  library: string;
  lastActivity: string;
  type: 'workspace';
}

interface IManageFolder {
  id: string;
  name: string;
  library: string;
  workspace: string;
  type: 'folder';
}

interface IManageDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  created: string;
  modified: string;
  author: string;
  extension: string;
  version: string;
}

interface IManageDocumentPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentsSelected: (documents: IManageDocument[], workspaceId: string, folderId: string) => void;
  selectedDocuments: IManageDocument[];
}

export const IManageDocumentPicker: React.FC<IManageDocumentPickerProps> = ({
  isOpen,
  onClose,
  onDocumentsSelected,
  selectedDocuments
}) => {
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [folders, setFolders] = useState<IManageFolder[]>([]);
  const [documents, setDocuments] = useState<IManageDocument[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLevel, setCurrentLevel] = useState<'libraries' | 'workspaces' | 'folders' | 'documents'>('libraries');
  const [selectedLibrary, setSelectedLibrary] = useState<Library | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<IManageFolder | null>(null);
  const [localSelectedDocs, setLocalSelectedDocs] = useState<IManageDocument[]>([]);

  const loadLibraries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get('/api/imanage/libraries');
      const libraryData = response.libraries || (Array.isArray(response) ? response : []);
      setLibraries(libraryData);
      setFilteredItems(libraryData);
      setCurrentLevel('libraries');
    } catch (error: any) {
      setError(error.message || 'Failed to load libraries');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkspaces = async (library: Library) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(`/api/imanage/libraries/${library.id}/workspaces`);
      const workspaceData = response.workspaces || (Array.isArray(response) ? response : []);
      setWorkspaces(workspaceData);
      setFilteredItems(workspaceData);
      setSelectedLibrary(library);
      setCurrentLevel('workspaces');
    } catch (error: any) {
      setError(error.message || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async (workspace: Workspace) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(`/api/imanage/libraries/${selectedLibrary!.id}/workspaces/${workspace.id}/folders`);
      const folderData = response.folders || (Array.isArray(response) ? response : []);
      setFolders(folderData);
      setFilteredItems(folderData);
      setSelectedWorkspace(workspace);
      setCurrentLevel('folders');
    } catch (error: any) {
      setError(error.message || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async (folder: IManageFolder) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(`/api/imanage/libraries/${selectedLibrary!.id}/folders/${folder.id}/documents`);
      const documentData = response.documents || (Array.isArray(response) ? response : []);
      setDocuments(documentData);
      setFilteredItems(documentData);
      setSelectedFolder(folder);
      setCurrentLevel('documents');
    } catch (error: any) {
      setError(error.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadLibraries();
      setSearchTerm('');
      setLocalSelectedDocs([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = filteredItems.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    } else {
      if (currentLevel === 'libraries') setFilteredItems(libraries);
      else if (currentLevel === 'workspaces') setFilteredItems(workspaces);
      else if (currentLevel === 'folders') setFilteredItems(folders);
      else if (currentLevel === 'documents') setFilteredItems(documents);
    }
  }, [searchTerm, libraries, workspaces, folders, documents, currentLevel]);

  const handleItemClick = (item: any) => {
    if (item.type === 'library') {
      loadWorkspaces(item as Library);
    } else if (item.type === 'workspace') {
      loadFolders(item as Workspace);
    } else if (item.type === 'folder') {
      loadDocuments(item as IManageFolder);
    } else {
      // Document selection
      const document = item as IManageDocument;
      const isSelected = localSelectedDocs.some(d => d.id === document.id);
      if (isSelected) {
        setLocalSelectedDocs(prev => prev.filter(d => d.id !== document.id));
      } else {
        setLocalSelectedDocs(prev => [...prev, document]);
      }
    }
  };

  const handleConfirmSelection = () => {
    if (localSelectedDocs.length > 0 && selectedWorkspace && selectedFolder) {
      onDocumentsSelected(localSelectedDocs, selectedWorkspace.id, selectedFolder.id);
    }
    onClose();
  };

  const getIcon = (item: any) => {
    switch (item.type) {
      case 'library': return <PiBooks size={24} />;
      case 'workspace': return <PiBriefcase size={24} />;
      case 'folder': return <PiFolder size={24} />;
      default: return <PiFileText size={24} />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Select Documents from iManage</Typography>
        <Typography variant="body2" color="text.secondary">
          {currentLevel === 'documents' ? 'Select documents to upload' : 'Navigate: Library → Workspace → Folder → Documents'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {localSelectedDocs.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Selected Documents ({localSelectedDocs.length}):
            </Typography>
            {localSelectedDocs.map((doc) => (
              <Typography key={doc.id} variant="body2">
                • {doc.name}
              </Typography>
            ))}
          </Box>
        )}

        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component="button" onClick={() => loadLibraries()}>Libraries</Link>
          {selectedLibrary && (
            <Link component="button" onClick={() => loadWorkspaces(selectedLibrary)}>
              {selectedLibrary.name}
            </Link>
          )}
          {selectedWorkspace && (
            <Link component="button" onClick={() => loadFolders(selectedWorkspace)}>
              {selectedWorkspace.name}
            </Link>
          )}
          {selectedFolder && (
            <Typography>{selectedFolder.name}</Typography>
          )}
        </Breadcrumbs>
        
        <TextField
          fullWidth
          placeholder={`Search ${currentLevel}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PiMagnifyingGlass size={20} />
              </InputAdornment>
            ),
          }}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ textAlign: 'center', p: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <List>
            {filteredItems.map((item) => {
              const isDocument = currentLevel === 'documents';
              const isSelected = isDocument && localSelectedDocs.some(d => d.id === item.id);
              
              return (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleItemClick(item)}
                    sx={{
                      bgcolor: isSelected ? 'primary.main' : 'transparent',
                      color: isSelected ? 'primary.contrastText' : 'inherit',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                      }
                    }}
                  >
                    {isDocument && (
                      <Checkbox
                        checked={isSelected}
                        sx={{ mr: 1 }}
                      />
                    )}
                    <ListItemIcon>
                      {getIcon(item)}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      secondary={
                        isDocument 
                          ? `${formatFileSize(item.size)} • ${item.extension?.toUpperCase()} • ${item.author}`
                          : item.description || item.lastActivity
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {localSelectedDocs.length > 0 && (
          <Button 
            onClick={handleConfirmSelection} 
            variant="contained"
          >
            Select {localSelectedDocs.length} Document{localSelectedDocs.length > 1 ? 's' : ''}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};